import Modal from "components/atom/Modal.tsx";
import { useCallback, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "config/store";
import Button from "components/atom/button/Button.tsx";
import markerPng from "assets/images/marker.png";
import dayjs from "dayjs";
import { searchActions } from "config/store/modules/search.ts";
import { useMount, useUnmount } from "react-use";
import { useTranslation } from "react-i18next";
import { STORAGE_COOKIE_SETTINGS_KEY, STORAGE_USER_LOCATION_KEY } from "utils/data/global.ts";
import { authActions } from "config/store/modules/auth.ts";
import { USER_LOG_TYPES } from "globals/constants.ts";

const now = dayjs();

const COOKIE_VERSION = "1.0.0";

let localLastLocation: {
  lat: number;
  lng: number;
} | null = null;

function ShareUserLocationAutomaticModal() {
  const [isOpen, setIsOpen] = useState(false);//start with false so we dont see flash
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const userLocation = useAppSelector(state => state.search.userLocation);
  const watchRefId = useRef<number | null>(null);


  const onSuccess = useCallback((position: GeolocationPosition) => {
    const location = {
      lat: position.coords.latitude,
      lng: position.coords.longitude
    };

    window.localStorage.setItem(STORAGE_USER_LOCATION_KEY, JSON.stringify({
      location: location,
      createdAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
    }));

    //for now integrate cookie too
    window.localStorage.setItem(STORAGE_COOKIE_SETTINGS_KEY, JSON.stringify({
      accept: true,
      version: COOKIE_VERSION,
      createdAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
    }));

    const userLogLocation: {
      lat: number;
      lng: number;
    } = {
      lat: parseFloat(location.lat.toFixed(5)),
      lng: parseFloat(location.lng.toFixed(5))
    };


    const hasChanged = Boolean(!localLastLocation || (localLastLocation && (userLogLocation.lat !== localLastLocation.lat || userLogLocation.lng !== localLastLocation.lng)));

    dispatch(searchActions.setLocation(location));

    localLastLocation = userLogLocation;
    if (hasChanged) {
      dispatch(authActions.sendUserLog(USER_LOG_TYPES.LOCATION_CHANGED, {
        latitude: userLogLocation.lat,
        longitude: userLogLocation.lng
      }));
    }
    setIsOpen(false);
  }, [dispatch]);

  const onError = useCallback((err: any) => {
    //error, save null
    window.localStorage.setItem(STORAGE_USER_LOCATION_KEY, JSON.stringify({
      location: null,
      createdAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
    }));

    window.localStorage.setItem(STORAGE_COOKIE_SETTINGS_KEY, JSON.stringify({
      accept: false,
      version: COOKIE_VERSION,
      createdAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
    }));

    setIsOpen(false);
  }, []);

  const getUserLocation = useCallback(() => {
    navigator.geolocation.getCurrentPosition(onSuccess, onError, {
      enableHighAccuracy: false
    });

    if (watchRefId.current) navigator.geolocation.clearWatch(watchRefId.current);
    watchRefId.current = navigator.geolocation.watchPosition(onSuccess, onError, {
      enableHighAccuracy: false
    });
  }, [onError, onSuccess]);

  useMount(() => {
    const lastLocation = window.localStorage.getItem(STORAGE_USER_LOCATION_KEY);

    if (!lastLocation) {
      setIsOpen(true);
      return;
    }

    const parsed: unknown = JSON.parse(lastLocation);

    if (!parsed || !parsed.createdAt) {
      setIsOpen(true);
      return;
    }

    const createdAt = dayjs(parsed.createdAt);

    //if 3 month old, ask again with popup
    if (now.diff(createdAt, "month") >= 3) {
      setIsOpen(true);
      return;
    }

    //if we got a location, set it
    if (parsed.location) {


      const userLogLocation: {
        lat: number;
        lng: number;
      } = {
        lat: parseFloat(parsed.location.lat.toFixed(5)),
        lng: parseFloat(parsed.location.lng.toFixed(5))
      };


      const hasChanged = Boolean(!localLastLocation || (localLastLocation && (userLogLocation.lat !== localLastLocation.lat || userLogLocation.lng !== localLastLocation.lng)));
      dispatch(searchActions.setLocation(parsed.location));
      localLastLocation = userLogLocation;
      if (hasChanged) {
        dispatch(authActions.sendUserLog(USER_LOG_TYPES.LOCATION_CHANGED, {
          latitude: userLogLocation.lat,
          longitude: userLogLocation.lng
        }));
      }
    }


    //refetch so we can get the latest location
    getUserLocation();//we got permission since localStorage got data once, try to get it now.
  });

  useUnmount(() => {
    if (watchRefId.current) {
      navigator.geolocation.clearWatch(watchRefId.current);
    }
  });

  const onAcceptUserLocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      return;
    }

    getUserLocation();
  }, [getUserLocation]);

  //if location is impossible dont show modal
  if (!("geolocation" in navigator)) {
    return null;
  }

  //already got the location
  if (userLocation) {
    return null;
  }

  if (!isOpen) {
    return null;
  }


  return (
    <Modal flexibleHeight className="-share-user-location" onClose={() => setIsOpen(false)}>
      <div className="w-full">
        <img src={markerPng} className="mx-auto block h-[200px] w-auto" />
        <h2 className="pb-3 text-center font-semibold">
          {t("popup.location.title")}
        </h2>
        <p className="pb-12 text-center text-gray-400">
          {t("popup.location.description")}
        </p>

        <Button className="block w-full" onClick={onAcceptUserLocation}>
          {t("popup.location.accept")}
        </Button>

        <Button style="text" small className="block w-full mt-3" onClick={() => setIsOpen(false)}>
          {t("popup.location.refuse")}
        </Button>
      </div>
    </Modal>
  );
}


export default ShareUserLocationAutomaticModal;
